import {
  ScoreModalBody,
  ScoreModalFooter,
  ScoreModalShell,
} from '@/components/custom/ScoreModalLayout';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CalendarDateFilter } from '@/components/programming/CalendarDateFilter';
import { CalendarView } from '@/components/programming/CalendarView';
import { type ProgrammingViewMode, ViewToggle } from '@/components/programming/ViewToggle';
import { WorkoutAttributionBadge } from '@/components/programming/WorkoutAttributionBadge';
import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import { filterByCalendarDate, getCalendarDate } from '@/components/programming/calendarNavigation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useIndividualsApi } from '@/hooks/useIndividualsApi';
import { usePermissions } from '@/hooks/usePermissions';
import { useSubmissionLock } from '@/hooks/useSubmissionLock';
import type { WorkoutAttribution } from '@ergshoot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  CheckCircle,
  Clock,
  Dumbbell,
  Hash,
  Loader2,
  Medal,
  RefreshCcw,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface Workout {
  id: string;
  name: string;
  description: string;
  workout_date?: string;
  track: { id: string; name: string } | null;
  scoring_type?: string;
  movements?: string | null;
  equipment?: string | null;
  duration_minutes?: number | null;
  scoring_direction?: string | null;
  category?: string | null;
  coach_notes?: string | null;
  my_score?: { value: number; unit: string; notes?: string | null; logged_at?: string } | null;
  attribution?: WorkoutAttribution | null;
  created_at?: string | null;
  created_by_username?: string | null;
}

interface IndividualScorePayload {
  workout_id: string;
  score_value: number;
  score_unit: string;
  notes?: string;
}

/** Parse a field that may be a JSON array string or pipe-delimited string into lines. */
function parseListField(raw: string): string[] {
  const trimmed = raw.trim();
  if (trimmed.startsWith('[')) {
    try {
      const arr: unknown[] = JSON.parse(trimmed);
      return arr.map((item) => String(item));
    } catch {
      // fall through to pipe splitting
    }
  }
  return trimmed
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
}

// BUG-APR30-005 — extracted card so we can reuse it across Past / Today /
// Upcoming sections. Mirrors MemberWorkouts.tsx WorkoutCard pattern.
function IndividualWorkoutCard({
  workout,
  onSelect,
  canLogScores,
}: {
  workout: Workout;
  onSelect: (w: Workout) => void;
  canLogScores: boolean;
}) {
  const ScoringIcon = getScoringTypeIcon(workout.scoring_type || 'reps');
  const hasScore = !!workout.my_score;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-xl">{workout.name}</CardTitle>
            <CardDescription className="flex items-center gap-2">
              <Badge variant="outline">
                <Dumbbell className="h-3 w-3 mr-1" />
                {workout.track?.name || 'Workout'}
              </Badge>
              <Badge variant="secondary">
                <ScoringIcon className="h-3 w-3 mr-1" />
                {getScoringTypeLabel(workout.scoring_type || 'reps')}
              </Badge>
              <WorkoutAttributionBadge attribution={workout.attribution} />
            </CardDescription>
            <WorkoutCreatorMetadata
              attribution={workout.attribution}
              created_at={workout.created_at}
              created_by_username={workout.created_by_username}
            />
          </div>
          {hasScore && (
            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
              <CheckCircle className="h-3 w-3 mr-1" />
              Completed
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Workout</h4>
          <pre className="whitespace-pre-wrap font-mono text-sm text-foreground bg-muted/50 border border-border rounded-md p-3">
            {workout.description}
          </pre>
        </div>
        {workout.movements && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Movements</h4>
            <pre className="whitespace-pre-wrap font-mono text-sm text-foreground bg-muted/50 border border-border rounded-md p-3">
              {parseListField(workout.movements).join('\n')}
            </pre>
          </div>
        )}
        {workout.equipment && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Equipment</h4>
            <div className="flex flex-wrap gap-1">
              {parseListField(workout.equipment).map((item) => (
                <Badge key={item} variant="outline">
                  {item}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {workout.coach_notes && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Coach&apos;s Notes</h4>
            <p className="text-sm italic text-muted-foreground">{workout.coach_notes}</p>
          </div>
        )}
        {!canLogScores ? null : hasScore ? (
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
            <div>
              <p className="text-sm text-muted-foreground">Your Score</p>
              <p className="text-2xl font-bold text-primary">
                {workout.my_score
                  ? formatScore(workout.my_score.value, workout.my_score.unit)
                  : 'N/A'}
              </p>
            </div>
            <Button variant="outline" onClick={() => onSelect(workout)}>
              Update Score
            </Button>
          </div>
        ) : (
          <Button className="w-full" onClick={() => onSelect(workout)}>
            <Medal className="h-4 w-4 mr-2" />
            Log Score
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Helpers — extracted so IndividualWorkoutCard can use them too.
function formatScore(value: number, unit: string): string {
  if (unit === 'time' || unit === 'seconds') {
    const mins = Math.floor(value / 60);
    const secs = value % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return `${value} ${unit}`;
}

function getScoringTypeLabel(type: string): string {
  switch (type) {
    case 'time':
      return 'For Time';
    case 'reps':
      return 'For Reps';
    case 'rounds':
      return 'Rounds + Reps';
    case 'points':
      return 'Points';
    default:
      return type;
  }
}

function getScoringTypeIcon(type: string) {
  switch (type) {
    case 'time':
      return Clock;
    case 'reps':
      return Hash;
    case 'rounds':
      return RefreshCcw;
    default:
      return Medal;
  }
}

export default function IndividualWorkouts() {
  const { hasPermission } = usePermissions();
  const canLogScores = hasPermission('log_scores');
  const individualsApi = useIndividualsApi();
  const queryClient = useQueryClient();
  const [selectedWorkout, setSelectedWorkout] = useState<Workout | null>(null);
  const [scoreValue, setScoreValue] = useState('');
  const [scoreMinutes, setScoreMinutes] = useState('');
  const [scoreSeconds, setScoreSeconds] = useState('');
  const [scoreNotes, setScoreNotes] = useState('');
  const scoreSubmission = useSubmissionLock<IndividualScorePayload>();

  const [viewMode, setViewMode] = useState<ProgrammingViewMode>(() => {
    return (localStorage.getItem('ergshoot-individual-view') as ProgrammingViewMode) || 'list';
  });
  const [calendarDateFilter, setCalendarDateFilter] = useState<string | null>(null);
  const handleViewModeChange = (mode: ProgrammingViewMode) => {
    setViewMode(mode);
    if (mode === 'calendar') setCalendarDateFilter(null);
    localStorage.setItem('ergshoot-individual-view', mode);
  };

  const todayStr = new Date().toISOString().split('T')[0] || '';
  const [showAllPast, setShowAllPast] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['individual-workouts'],
    queryFn: async () => {
      const response = await individualsApi.getWorkouts();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const logScoreMutation = useMutation({
    mutationFn: async (attempt: { payload: IndividualScorePayload; idempotencyKey: string }) => {
      const response = await individualsApi.logScore(attempt.payload, attempt.idempotencyKey);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      scoreSubmission.reset();
      toast.success('Score logged successfully!');
      setSelectedWorkout(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['individual-workouts'] });
      queryClient.invalidateQueries({ queryKey: ['individual-dashboard'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to log score');
      scoreSubmission.release();
    },
  });

  const resetForm = () => {
    setScoreValue('');
    setScoreMinutes('');
    setScoreSeconds('');
    setScoreNotes('');
  };

  const handleLogScore = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWorkout) return;

    const scoringType = selectedWorkout.scoring_type || 'reps';
    let value: number;
    let unit: string;

    if (scoringType === 'time') {
      const mins = Number.parseInt(scoreMinutes, 10) || 0;
      const secs = Number.parseInt(scoreSeconds, 10) || 0;
      value = mins * 60 + secs;
      unit = 'time';
    } else {
      value = Number.parseInt(scoreValue, 10);
      unit = scoringType;
    }

    if (Number.isNaN(value) || value < 0) {
      toast.error('Please enter a valid score');
      return;
    }

    const payload: IndividualScorePayload = {
      workout_id: selectedWorkout.id,
      score_value: value,
      score_unit: unit,
      notes: scoreNotes || undefined,
    };
    const attempt = scoreSubmission.begin(payload);
    if (!attempt) return;
    logScoreMutation.mutate(attempt);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    // If in View-as mode, show friendly preview message instead of error
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
          <p className="text-destructive">Failed to load workouts</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const workouts: Workout[] = (data?.workouts || []) as Workout[];

  // BUG-APR30-005 — bucket by date relative to today. Strict `< todayStr`
  // for past so future dates can never leak in (BUG-APR07-006 pattern).
  const dayOf = (w: Workout): string => {
    const s = w.workout_date ?? '';
    return s.split('T')[0] ?? '';
  };
  const todayWorkouts = workouts.filter((w) => dayOf(w) === todayStr);
  const pastWorkouts = workouts
    .filter((w) => {
      const d = dayOf(w);
      return d !== '' && d < todayStr;
    })
    .sort((a, b) => (dayOf(a) < dayOf(b) ? 1 : -1));
  const futureWorkouts = workouts
    .filter((w) => {
      const d = dayOf(w);
      return d !== '' && d > todayStr;
    })
    .sort((a, b) => (dayOf(a) < dayOf(b) ? -1 : 1));
  const visiblePast = showAllPast ? pastWorkouts : pastWorkouts.slice(0, 5);
  const selectedDateWorkouts = filterByCalendarDate(
    workouts,
    calendarDateFilter,
    (workout) => workout.workout_date
  );

  const handleLogScoreClick = (w: Workout) => {
    setSelectedWorkout(w);
    if (w.my_score) {
      if (w.my_score.unit === 'time' || w.my_score.unit === 'seconds') {
        const mins = Math.floor(w.my_score.value / 60);
        const secs = w.my_score.value % 60;
        setScoreMinutes(mins.toString());
        setScoreSeconds(secs.toString());
      } else {
        setScoreValue(w.my_score.value.toString());
      }
    }
  };

  const handleCalendarDateClick = (date: string) => {
    setCalendarDateFilter(date);
    handleViewModeChange('list');
  };

  const formatRowDate = (d?: string) =>
    d
      ? new Date(`${d.split('T')[0]}T00:00:00`).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
      : '';

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Workouts</h1>
          <p className="mt-1 text-muted-foreground">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <ViewToggle viewMode={viewMode} onChange={handleViewModeChange} />
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <CalendarView
          workouts={workouts.map((w) => ({
            id: w.id,
            name: w.name,
            category: w.category ?? undefined,
            scoring_type: w.scoring_type || 'reps',
            scheduled_date: w.workout_date || '',
          }))}
          loading={isLoading}
          onDayClick={handleCalendarDateClick}
          onWorkoutClick={(calWorkout) =>
            handleCalendarDateClick(getCalendarDate(calWorkout.scheduled_date))
          }
        />
      )}

      {/* List View */}
      {viewMode === 'list' && calendarDateFilter && (
        <section className="space-y-4">
          <CalendarDateFilter
            date={calendarDateFilter}
            onClear={() => setCalendarDateFilter(null)}
          />
          {selectedDateWorkouts.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No workouts scheduled for this date.
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {selectedDateWorkouts.map((workout) => (
                <IndividualWorkoutCard
                  key={workout.id}
                  workout={workout}
                  onSelect={handleLogScoreClick}
                  canLogScores={canLogScores}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {viewMode === 'list' && !calendarDateFilter && (
        <div className="space-y-8">
          {/* Today */}
          <section>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Today
            </h2>
            {todayWorkouts.length === 0 ? (
              <Card>
                <CardContent className="py-8">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="rounded-full bg-muted p-4 mb-4">
                      <Dumbbell className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-1">Rest day</h3>
                    <p className="text-muted-foreground text-sm">
                      No workout scheduled for today — recover well.
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {todayWorkouts.map((workout) => (
                  <IndividualWorkoutCard
                    key={workout.id}
                    workout={workout}
                    onSelect={handleLogScoreClick}
                    canLogScores={canLogScores}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Past */}
          {pastWorkouts.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-muted-foreground" />
                Past Workouts
                <Badge variant="outline" className="ml-1">
                  {pastWorkouts.length}
                </Badge>
              </h2>
              <div className="grid gap-4">
                {visiblePast.map((workout) => (
                  <div key={workout.id} className="space-y-1">
                    <p className="text-xs text-muted-foreground px-1">
                      {formatRowDate(workout.workout_date)}
                    </p>
                    <IndividualWorkoutCard
                      workout={workout}
                      onSelect={handleLogScoreClick}
                      canLogScores={canLogScores}
                    />
                  </div>
                ))}
              </div>
              {pastWorkouts.length > 5 && (
                <Button
                  variant="ghost"
                  className="w-full mt-4"
                  onClick={() => setShowAllPast((v) => !v)}
                >
                  {showAllPast ? 'Show less' : `Show ${pastWorkouts.length - 5} more past workouts`}
                </Button>
              )}
            </section>
          )}

          {/* Upcoming (BUG-APR30-005 — strict `> todayStr` so today never leaks here) */}
          {futureWorkouts.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-primary" />
                Upcoming Workouts
                <Badge variant="outline" className="ml-1">
                  {futureWorkouts.length}
                </Badge>
              </h2>
              <div className="grid gap-4">
                {futureWorkouts.slice(0, 7).map((workout) => (
                  <div key={workout.id} className="space-y-1">
                    <p className="text-xs text-muted-foreground px-1">
                      {formatRowDate(workout.workout_date)}
                    </p>
                    <IndividualWorkoutCard
                      workout={workout}
                      onSelect={handleLogScoreClick}
                      canLogScores={canLogScores}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Log Score Modal */}
      {canLogScores && selectedWorkout && (
        <ScoreModalShell
          ariaLabel={`Log score for ${selectedWorkout.name}`}
          onClose={() => {
            setSelectedWorkout(null);
            resetForm();
          }}
        >
          <Card className="flex max-h-[calc(100dvh-2rem)] min-h-0 flex-col overflow-hidden animate-fade-in">
            <CardHeader className="shrink-0">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>Log Score</CardTitle>
                  <CardDescription>{selectedWorkout.name}</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={scoreSubmission.isLocked}
                  onClick={() => {
                    setSelectedWorkout(null);
                    resetForm();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col p-0">
              <form onSubmit={handleLogScore} className="flex min-h-0 flex-1 flex-col">
                <ScoreModalBody className="space-y-4 px-6 pb-4">
                  {selectedWorkout.scoring_type === 'time' ||
                  selectedWorkout.scoring_type === 'seconds' ? (
                    <div className="space-y-2">
                      <Label>Time (MM:SS)</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="number"
                          placeholder="MM"
                          min="0"
                          max="99"
                          value={scoreMinutes}
                          onChange={(e) => setScoreMinutes(e.target.value)}
                          className="w-24"
                        />
                        <span className="text-xl font-bold">:</span>
                        <Input
                          type="number"
                          placeholder="SS"
                          min="0"
                          max="59"
                          value={scoreSeconds}
                          onChange={(e) => setScoreSeconds(e.target.value)}
                          className="w-24"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="score-value">
                        {getScoringTypeLabel(selectedWorkout.scoring_type || 'reps')}
                      </Label>
                      <Input
                        id="score-value"
                        type="number"
                        placeholder="Enter your score"
                        min="0"
                        value={scoreValue}
                        onChange={(e) => setScoreValue(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="score-notes">Notes (optional)</Label>
                    <textarea
                      id="score-notes"
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Rx, scaled, modifications..."
                      value={scoreNotes}
                      onChange={(e) => setScoreNotes(e.target.value)}
                    />
                  </div>
                </ScoreModalBody>

                <ScoreModalFooter className="flex gap-2">
                  <Button type="submit" className="flex-1" disabled={scoreSubmission.isLocked}>
                    {scoreSubmission.isLocked ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Save Score
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={scoreSubmission.isLocked}
                    onClick={() => {
                      setSelectedWorkout(null);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </ScoreModalFooter>
              </form>
            </CardContent>
          </Card>
        </ScoreModalShell>
      )}
    </DashboardLayout>
  );
}
